/**
 * Secure Cookie Utilities for JWT Token Storage
 *
 * This module provides utilities for storing JWT tokens in secure cookies
 * with proper HttpOnly, Secure, and SameSite attributes.
 */

export interface CookieOptions {
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  maxAge?: number; // seconds
  expires?: Date;
  path?: string;
  domain?: string;
}

/**
 * Set a secure cookie with proper encoding
 */
export const setSecureCookie = (name: string, value: string, options: CookieOptions = {}): boolean => {
  try {
    // Default secure options
    const defaultOptions: CookieOptions = {
      httpOnly: false, // Note: Cannot set HttpOnly from client-side JavaScript
      secure: window.location.protocol === "https:",
      sameSite: "Strict",
      path: "/",
      maxAge: 3600, // 1 hour default
    };

    const finalOptions = { ...defaultOptions, ...options };

    // URL encode the value to handle special characters
    const encodedValue = encodeURIComponent(value);

    // Build cookie string
    let cookieString = `${name}=${encodedValue}`;

    if (finalOptions.path) {
      cookieString += `; Path=${finalOptions.path}`;
    }

    if (finalOptions.domain) {
      cookieString += `; Domain=${finalOptions.domain}`;
    }

    if (finalOptions.maxAge) {
      cookieString += `; Max-Age=${finalOptions.maxAge}`;
    }

    if (finalOptions.expires) {
      cookieString += `; Expires=${finalOptions.expires.toUTCString()}`;
    }

    if (finalOptions.secure) {
      cookieString += `; Secure`;
    }

    if (finalOptions.sameSite) {
      cookieString += `; SameSite=${finalOptions.sameSite}`;
    }

    // Note: HttpOnly cannot be set from client-side JavaScript
    // This would need to be set by the server
    if (finalOptions.httpOnly) {
      console.warn("HttpOnly cookies cannot be set from client-side JavaScript");
    }

    document.cookie = cookieString;

    // Verify the cookie was set by trying to read it back
    const verification = getCookie(name);
    return verification === value;
  } catch (error) {
    console.error("Failed to set secure cookie:", error);
    return false;
  }
};

/**
 * Get a cookie value with proper decoding
 */
export const getCookie = (name: string): string | null => {
  try {
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split("=");

      if (cookieName === name && cookieValue) {
        // URL decode the value
        return decodeURIComponent(cookieValue);
      }
    }

    return null;
  } catch (error) {
    console.error("Failed to get cookie:", error);
    return null;
  }
};

/**
 * Delete a cookie by setting it to expire in the past
 */
export const deleteCookie = (name: string, options: Pick<CookieOptions, "path" | "domain"> = {}): boolean => {
  try {
    const defaultOptions = {
      path: "/",
    };

    const finalOptions = { ...defaultOptions, ...options };

    let cookieString = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 UTC`;

    if (finalOptions.path) {
      cookieString += `; Path=${finalOptions.path}`;
    }

    if (finalOptions.domain) {
      cookieString += `; Domain=${finalOptions.domain}`;
    }

    document.cookie = cookieString;

    // Verify the cookie was deleted
    const verification = getCookie(name);
    return verification === null;
  } catch (error) {
    console.error("Failed to delete cookie:", error);
    return false;
  }
};

/**
 * Check if cookies are supported and enabled
 */
export const areCookiesSupported = (): boolean => {
  try {
    const testCookieName = "ps_cookie_test";
    const testValue = "test";

    document.cookie = `${testCookieName}=${testValue}; Path=/; SameSite=Strict`;
    const result = document.cookie.includes(`${testCookieName}=${testValue}`);

    // Clean up test cookie
    document.cookie = `${testCookieName}=; Expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/`;

    return result;
  } catch {
    return false;
  }
};

/**
 * Get all cookies as an object
 */
export const getAllCookies = (): Record<string, string> => {
  try {
    const cookies: Record<string, string> = {};
    const cookieString = document.cookie;

    if (!cookieString) {
      return cookies;
    }

    cookieString.split(";").forEach((cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    });

    return cookies;
  } catch (error) {
    console.error("Failed to get all cookies:", error);
    return {};
  }
};

/**
 * Calculate expiration date based on token expires_in
 */
export const calculateExpirationDate = (expiresInSeconds: number): Date => {
  const now = new Date();
  return new Date(now.getTime() + expiresInSeconds * 1000);
};

/**
 * Validate cookie size (browsers typically limit to 4KB per cookie)
 */
export const validateCookieSize = (name: string, value: string): boolean => {
  const encodedValue = encodeURIComponent(value);
  const totalSize = name.length + encodedValue.length;
  const maxSize = 4096; // 4KB limit

  if (totalSize > maxSize) {
    console.warn(`Cookie size (${totalSize} bytes) exceeds recommended limit (${maxSize} bytes)`);
    return false;
  }

  return true;
};
